import type { BeadSizeId } from './types';

/**
 * Physical bead dimensions, in millimetres.
 *
 * `len` is the extent along the hole (the direction the thread runs) and `dia`
 * is the extent across it. Seed beads are wider than they are long, which is
 * why loom and peyote charts have taller-than-wide cells and brick charts have
 * wider-than-tall ones — the bead is simply turned 90 degrees.
 *
 * Figures are nominal manufacturer sizes; real beads vary by a few percent
 * between colours and finishes, so treat the physical size readout and the
 * gram estimates as close approximations rather than exact.
 */
export interface BeadSpec {
  id: BeadSizeId;
  label: string;
  /** Across the hole, mm. */
  dia: number;
  /** Along the hole, mm. */
  len: number;
  /** Approximate beads per gram, used for the shopping list. */
  perGram: number;
  /** Cylinder beads sit flush; round beads are drawn with more curvature. */
  shape: 'cylinder' | 'round' | 'cube';
}

export const BEAD_SPECS: Record<BeadSizeId, BeadSpec> = {
  delica11: { id: 'delica11', label: 'Delica 11/0 (cylinder)', dia: 1.6, len: 1.3, perGram: 200, shape: 'cylinder' },
  delica10: { id: 'delica10', label: 'Delica 10/0 (cylinder)', dia: 2.2, len: 1.7, perGram: 110, shape: 'cylinder' },
  delica8: { id: 'delica8', label: 'Delica 8/0 (cylinder)', dia: 3.1, len: 2.5, perGram: 47, shape: 'cylinder' },
  round15: { id: 'round15', label: 'Seed 15/0 (round)', dia: 1.5, len: 1.0, perGram: 330, shape: 'round' },
  round11: { id: 'round11', label: 'Seed 11/0 (round)', dia: 2.2, len: 1.5, perGram: 110, shape: 'round' },
  round8: { id: 'round8', label: 'Seed 8/0 (round)', dia: 3.0, len: 2.2, perGram: 38, shape: 'round' },
  round6: { id: 'round6', label: 'Seed 6/0 (round)', dia: 4.0, len: 3.3, perGram: 15, shape: 'round' },
  square: { id: 'square', label: 'Cube 1.8mm', dia: 1.8, len: 1.8, perGram: 90, shape: 'cube' },
};

export const BEAD_SIZE_LIST = Object.values(BEAD_SPECS);
